/**
 * @file ./models/mapper/formatadoresDanfe.js
 *
 * Formatação e mascaramento dos valores da NF-e para exibição no DANFE.
 *
 * Regra transversal do MOC 7.0 (Anexo II): o conteúdo dos campos é impresso
 * em **caixa alta**. As funções daqui já devolvem o texto pronto para o
 * papel — o layout não deve reformatar nada.
 *
 * Nenhuma função lança: dado ausente vira string vazia. O DANFE tem campos
 * legitimamente vazios (IE isento, frete sem transportadora, nota sem
 * duplicata) e o motor de renderização não pode quebrar por causa deles.
 */

const SEM_VALOR = '';

/**
 * Extrai apenas os dígitos de um valor qualquer.
 *
 * @param {*} valor
 * @returns {string}
 */
function digitos(valor) {
  return String(valor ?? '').replace(/\D/g, '');
}

/**
 * Aplica a máscara de CNPJ (14 dígitos) ou CPF (11 dígitos), escolhendo pelo
 * comprimento. Valores fora desses tamanhos voltam como vieram — é melhor
 * imprimir um documento estrangeiro/atípico sem máscara do que suprimi-lo.
 *
 * @param {string} documento
 * @returns {string}
 */
export function mascararCnpjCpf(documento) {
  const numeros = digitos(documento);

  if (numeros.length === 14) {
    return numeros.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5',
    );
  }
  if (numeros.length === 11) {
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  return String(documento ?? SEM_VALOR);
}

/**
 * Aplica a máscara de CEP (99999-999).
 *
 * @param {string} cep
 * @returns {string}
 */
export function mascararCep(cep) {
  const numeros = digitos(cep);
  if (numeros.length !== 8) return String(cep ?? SEM_VALOR);
  return numeros.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Aplica a máscara de telefone com DDD. O XML da NF-e traz o telefone sem
 * separadores e com 10 (fixo) ou 11 (celular) dígitos.
 *
 * @param {string} telefone
 * @returns {string}
 */
export function mascararTelefone(telefone) {
  const numeros = digitos(telefone);

  if (numeros.length === 11) {
    return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (numeros.length === 10) {
    return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  return String(telefone ?? SEM_VALOR);
}

/**
 * Formata número decimal no padrão brasileiro (ponto de milhar, vírgula
 * decimal).
 *
 * @param {number|string} valor
 * @param {number} [casas=2] - Casas decimais fixas.
 * @returns {string}
 */
export function formatarNumero(valor, casas = 2) {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return SEM_VALOR;

  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

/**
 * Formata valor monetário (2 casas). Zero é impresso como `0,00` — o DANFE
 * exige o valor explícito nos campos de totalização, não campo em branco.
 *
 * @param {number|string} valor
 * @returns {string}
 */
export function formatarMoeda(valor) {
  return formatarNumero(valor, 2);
}

/**
 * Formata quantidade comercial/tributável (4 casas, conforme o layout 4.00).
 *
 * @param {number|string} valor
 * @returns {string}
 */
export function formatarQuantidade(valor) {
  return formatarNumero(valor, 4);
}

/**
 * Formata alíquota percentual (2 casas), sem o símbolo `%` — o rótulo da
 * coluna do DANFE já indica a unidade.
 *
 * @param {number|string} valor
 * @returns {string}
 */
export function formatarAliquota(valor) {
  return formatarNumero(valor, 2);
}

/**
 * Separa data e hora de um `dhEmi`/`dhSaiEnt` do layout 4.00
 * (`2026-07-29T11:06:00-03:00`).
 *
 * A leitura é feita por expressão regular, e não por `new Date()`, de
 * propósito: `Date` converteria o horário para o fuso da máquina que está
 * gerando o PDF, e o DANFE deve imprimir o horário local declarado pelo
 * emitente no XML.
 *
 * @param {string} dataHoraIso
 * @returns {{ data: string, hora: string }} Ambos vazios se não houver valor.
 */
export function separarDataHora(dataHoraIso) {
  const texto = String(dataHoraIso ?? '').trim();
  if (!texto) return { data: SEM_VALOR, hora: SEM_VALOR };

  const casamento = texto.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (!casamento) return { data: texto, hora: SEM_VALOR };

  const [, ano, mes, dia, hora, minuto, segundo] = casamento;

  return {
    data: `${dia}/${mes}/${ano}`,
    hora: hora ? `${hora}:${minuto}:${segundo ?? '00'}` : SEM_VALOR,
  };
}

/**
 * Formata apenas a data (dd/mm/aaaa).
 *
 * @param {string} dataHoraIso
 * @returns {string}
 */
export function formatarData(dataHoraIso) {
  return separarDataHora(dataHoraIso).data;
}

/**
 * Formata data e hora completas (dd/mm/aaaa hh:mm:ss).
 *
 * @param {string} dataHoraIso
 * @returns {string}
 */
export function formatarDataHora(dataHoraIso) {
  const { data, hora } = separarDataHora(dataHoraIso);
  return [data, hora].filter(Boolean).join(' ');
}

/**
 * Formata a data/hora do momento da impressão (dd/mm/aaaa hh:mm:ss), no fuso
 * da máquina que está gerando o PDF.
 *
 * Diferente de {@link formatarDataHora}, aqui não há um horário declarado no
 * XML a preservar — é o instante local da geração do PDF, então `new Date()`
 * é o que se quer.
 *
 * @returns {string}
 */
export function formatarDataHoraAtual() {
  const agora = new Date();
  const doisDigitos = (numero) => String(numero).padStart(2, '0');

  const data = [agora.getDate(), agora.getMonth() + 1, agora.getFullYear()]
    .map((parte, indice) => (indice === 2 ? parte : doisDigitos(parte)))
    .join('/');
  const hora = [agora.getHours(), agora.getMinutes(), agora.getSeconds()]
    .map(doisDigitos)
    .join(':');

  return `${data} ${hora}`;
}

/**
 * Formata a chave de acesso na máscara visual obrigatória do MOC: 11 blocos
 * de 4 dígitos separados por espaço.
 *
 * @param {string} chave - 44 dígitos.
 * @returns {string}
 */
export function formatarChaveEmBlocos(chave) {
  const numeros = digitos(chave);
  if (!numeros) return SEM_VALOR;
  return (numeros.match(/.{1,4}/g) ?? []).join(' ');
}

/**
 * Formata o número da NF-e no padrão do DANFE: 9 dígitos com ponto de
 * milhar (`000.010.599`).
 *
 * @param {string|number} numero
 * @returns {string}
 */
export function formatarNumeroNota(numero) {
  const numeros = digitos(numero);
  if (!numeros) return SEM_VALOR;
  return numeros.padStart(9, '0').replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3');
}

/**
 * Formata a série com 3 dígitos (`000`), como nos DANFEs homologados.
 *
 * @param {string|number} serie
 * @returns {string}
 */
export function formatarSerie(serie) {
  const numeros = digitos(serie);
  if (!numeros) return '000';
  return numeros.padStart(3, '0');
}

/**
 * Compõe o código de situação tributária impresso na coluna `CST` do quadro
 * de produtos: origem da mercadoria + CST (ou CSOSN, no Simples Nacional).
 *
 * @param {string} origem
 * @param {string} cst
 * @returns {string}
 */
export function comporCstComOrigem(origem, cst) {
  const codigoOrigem = digitos(origem);
  const codigoCst = String(cst ?? '').trim();
  if (!codigoCst) return SEM_VALOR;
  return `${codigoOrigem}${codigoCst}`;
}

/** Rótulos de `tpNF` (tipo de operação). */
const TIPOS_OPERACAO = {
  0: 'ENTRADA',
  1: 'SAÍDA',
};

/**
 * Rótulo do tipo de operação para o quadro central do DANFE.
 *
 * @param {string|number} tipoOperacao - `tpNF` (0 = entrada, 1 = saída).
 * @returns {{ codigo: string, rotulo: string, entrada: boolean }}
 */
export function descreverTipoOperacao(tipoOperacao) {
  const codigo = digitos(tipoOperacao) || '1';
  return {
    codigo,
    rotulo: TIPOS_OPERACAO[codigo] ?? SEM_VALOR,
    entrada: codigo === '0',
  };
}

/**
 * Rótulos abreviados de `modFrete`. O campo "FRETE POR CONTA" tem 90 pt e
 * imprime em 10 pt (mínimo do MOC 3.7.9): cabem cerca de 13 caracteres, o
 * que inviabiliza as descrições integrais do manual.
 */
const MODALIDADES_FRETE = {
  0: 'REMETENTE',
  1: 'DESTINAT',
  2: 'TERCEIROS',
  3: 'PROP/REM',
  4: 'PROP/DEST',
  9: 'SEM FRETE',
};

/**
 * Descreve a modalidade do frete (`modFrete`).
 *
 * @param {string|number} modalidade
 * @returns {string} Ex.: `0-EMITENTE`.
 */
export function descreverModalidadeFrete(modalidade) {
  const codigo = digitos(modalidade);
  if (codigo === '') return SEM_VALOR;
  const rotulo = MODALIDADES_FRETE[codigo];
  return rotulo ? `${codigo} - ${rotulo}` : codigo;
}

/**
 * Normaliza texto para impressão: caixa alta, espaços colapsados e sem
 * quebras de linha órfãs.
 *
 * @param {*} valor
 * @returns {string}
 */
export function caixaAlta(valor) {
  return String(valor ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/**
 * Normaliza texto para impressão em múltiplas linhas:
 * Converte `||` em quebras de linha (`\n`), normaliza espaços por linha e aplica caixa alta.
 *
 * @param {*} valor
 * @returns {string}
 */
export function caixaAltaMultilinha(valor) {
  if (!valor) return SEM_VALOR;
  return String(valor)
    .replace(/\r\n/g, '\n')
    .replace(/\|\|/g, '\n')
    .split('\n')
    .map((linha) => caixaAlta(linha))
    .join('\n')
    .trim();
}

/**
 * Junta partes de texto descartando as vazias — evita separadores soltos
 * ("CARUARU/ - ") quando um pedaço do endereço não veio no XML.
 *
 * @param {Array<*>} partes
 * @param {string} [separador=' ']
 * @returns {string}
 */
export function juntar(partes, separador = ' ') {
  return partes
    .map((parte) => String(parte ?? '').trim())
    .filter((parte) => parte !== '')
    .join(separador);
}
